/**
 * Bundle sanitization policy. The `manifest.json` `policyVersion` field
 * carries this number; bump it on any change to the redaction rules so
 * the support team can tell at a glance which rules were in effect when
 * a given bundle was produced.
 */
export const BUNDLE_POLICY_VERSION = 2;

export const DIAGNOSTIC_REDACTION_PATTERNS = {
  /**
   * Query parameter names whose value is replaced with '***'.
   * Case-insensitive matching (the sanitizer lowercases both sides).
   *
   * Covers:
   * - OAuth/OIDC: token, access_token, id_token, refresh_token, code,
   *   client_secret, assertion
   * - API-key conventions: api_key, apikey, bearer
   * - Session: session, sid, sessionid
   * - Generic: password, secret, signature, x-amz-signature (AWS URL
   *   signing)
   */
  forbiddenQueryParams: [
    'token',
    'access_token',
    'id_token',
    'refresh_token',
    'code',
    'client_secret',
    'assertion',
    'api_key',
    'apikey',
    'bearer',
    'session',
    'sid',
    'sessionid',
    'password',
    'secret',
    'signature',
    'x-amz-signature',
  ],

  /**
   * Header names that should be stripped if a payload ever contains a
   * `headers` field or carries headers within `extra`. Today no event
   * payload includes headers, but the sanitizer is wired so the
   * debug/deepDebug body-capture follow-up will be covered.
   */
  forbiddenHeaders: ['authorization', 'cookie', 'set-cookie'],

  /**
   * Object keys whose value is redacted to '***' wherever they appear
   * in a structured payload (request/response body excerpts, the
   * `extra` field of UserActionEvent, …). Case-insensitive matching.
   *
   * Two threat models converge here:
   * 1. User-controlled credentials a frontend sends with a login /
   *    payment / password-reset request: `password`, `cvv`, …
   * 2. Server-controlled PII / financial data in responses: account
   *    numbers, government IDs, card numbers, …
   *
   * The list intentionally overlaps with `forbiddenQueryParams` for
   * credential-style keys (token, secret, …) so a value cannot leak
   * via the body just because the producer chose to send it there
   * instead of as a query string.
   *
   * Bump `BUNDLE_POLICY_VERSION` when this list changes — the
   * support team relies on policyVersion to know which rules were in
   * effect for a given bundle.
   */
  forbiddenBodyKeys: [
    // Credentials
    'password',
    'passwd',
    'pwd',
    'currentpassword',
    'newpassword',
    'confirmpassword',
    'oldpassword',
    'secret',
    'clientsecret',
    'client_secret',
    'apisecret',
    'api_secret',
    'token',
    'accesstoken',
    'access_token',
    'idtoken',
    'id_token',
    'refreshtoken',
    'refresh_token',
    'apikey',
    'api_key',
    'sessionid',
    'session_id',
    'sessiontoken',
    'session_token',
    // Payment / card
    'cardnumber',
    'card_number',
    'creditcard',
    'credit_card',
    'cvv',
    'cvc',
    'cardcvv',
    'card_cvv',
    'pin',
    'iban',
    // Government / national IDs
    'ssn',
    'taxid',
    'tax_id',
    'nationalid',
    'national_id',
    'passportnumber',
    'passport_number',
  ],

  /**
   * JWT pattern: header.payload.signature, base64url-encoded. Anchored on
   * the 'eyJ' header prefix (base64url of '{"' that every JWT header starts
   * with) to avoid false positives on arbitrary dot-separated strings.
   */
  jwtPattern: /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
} as const;
